import * as React from "react";
import {PluginTemplates} from "../../../../utils/PluginTemplates";
import FalconHeader from "../../../../components/navigation/FalconHeader";
import {
    InstalmentsInfo,
    INTERACTION_PROCESS_MTA_CHANGE_PAYMENT_DAY,
    Policy,
    productHelper
} from "@ferry/journey-services";
import {lazyInject} from "@athena-portal/core";
import PaymentSchedule from "./paymentDetails/annual/PaymentSchedule";
import {calculateInstallmentPayment, getAmountWithDecimalDigits} from "../../../../CommonFunctions";
import {authorize, IWithRouterProps, Overlay, withRouter} from "@athena-portal/components";
import NotificationModal from "../../../../components/modals/NotificationModal";
import {Types} from "../../../../Types";
import type {ICustomerPortalService} from "../../../../services/ICustomerPortalService";
import {InfoBanner, WarningBanner, Footer} from "@ferry/portal-core";

interface IParams {
    policyNumber: string;
    [key: string]: string | undefined
}

interface IState {
    newPaymentDay: number;
    policy?: Policy;
    combinedInstalmentsInfo?: any;
    isOpen: boolean;
    showNoDaySelectedAlert: boolean;
    showOverlay: boolean;
    paymentDayOptions: any[];
    loadingDays: boolean;
    isDocumentGenerated: boolean;
}

@authorize
export class DirectDebitChangePaymentDayPagePlain extends React.Component<IWithRouterProps<IParams>, IState> {

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    private pluginTemplates = new PluginTemplates();
    private readonly policyNumber: string;

    constructor(props: IWithRouterProps<IParams>) {
        super(props);

        this.policyNumber = this.props.params.policyNumber;

        this.state = {
            newPaymentDay: 0,
            isOpen: false,
            showNoDaySelectedAlert: false,
            showOverlay: false,
            paymentDayOptions: [],
            loadingDays: true,
            isDocumentGenerated: false
        }

        this.updatePaymentDay = this.updatePaymentDay.bind(this);
        this.updatePreviewInstalmentPlan = this.updatePreviewInstalmentPlan.bind(this);
        this.saveAndConfirm = this.saveAndConfirm.bind(this);
        this.cancelChanges = this.cancelChanges.bind(this);
        this.closeNoPaymentDaySelectedModal = this.closeNoPaymentDaySelectedModal.bind(this)
        this.renderAlert = this.renderAlert.bind(this)
    }

    public componentDidMount(): void {
        this.pluginTemplates.setPageTitle("Change your payment day");

        this.customerPortalService
            .getPolicy(this.props.params.policyNumber)
            .subscribe((policy: Policy) => {
                this.setState({
                    policy
                }, () => {
                    this.fetchExistingCombinedInstalments();
                    this.fetchAvailablePaymentDays(policy);
                    this.setState({isOpen: productHelper.isWolfMotor(policy.interaction)});
                });
            });
    }

    public render(): React.ReactNode {
        const previousPage = `/dashboard/monthlyPaymentHistory/${this.props.params.policyNumber}`;
        const policy: any = this.state.policy;
        return (
            <>
                <FalconHeader/>
                <div className="plugin">
                    {this.pluginTemplates.renderBackReloadLink("Back", true, previousPage)}
                    {this.renderPageTitle()}
                    {this.renderHeadsUpPanel()}
                    {policy && productHelper.isWolfMotor(policy.interaction) && this.renderAvailablePaymentDaysBanner()}
                    {this.renderChangePaymentDatePanel()}
                    {this.renderNavigationButtons()}
                    {this.pluginTemplates.renderBackReloadLink("Back", false, previousPage)}
                </div>
                <Footer/>
                <Overlay show={this.state.showOverlay}/>
                {this.renderAlert()}
            </>
        );
    }

    private fetchExistingCombinedInstalments(): void {
        const policyNumber: string = this.state.policy?.policyNumber ?? "";
        if (policyNumber) {
            this.customerPortalService
                .getInstalmentsInfo(policyNumber, false)
                .subscribe(combinedInstalmentsInfo => this.setState({combinedInstalmentsInfo}));
        }
    }

    private fetchAvailablePaymentDays(policy: Policy): void {
        if (productHelper.isWolfMotor(policy.interaction)) {
            this.customerPortalService
                .getAvailablePaymentDays(this.props.params.policyNumber)
                .subscribe(paymentDayOptions => this.setState({paymentDayOptions},
                    () => this.setState({loadingDays: false})));
        } else {
            const options: string[] = ["1st", "2nd", "3rd"];
            for (let i = 4; i <= 28; i++) {
                options.push(`${i}th`);
            }
            this.setState({paymentDayOptions: options});
        }
    }

    private renderPageTitle(): React.ReactNode {
        return (
            <>
                <div className="text-group title plugin no-indent-desktop">
                    <h1>Change your payment day</h1>
                </div>
            </>
        );
    }

    private renderHeadsUpPanel(): React.ReactNode {
        const instalmentPlanText: string = this.state.policy?.interaction && productHelper.isWolfMotor(this.state.policy.interaction) ?
            "Please check your new schedule below as this change could result in two payments close together." :
            "Please click ‘View instalments plan’ below to check your schedule as this could result in two payments close together.";

        return (
            <InfoBanner wording={<><b>Heads up -</b> your change will take effect in a future month and won’t apply to your next payment. {instalmentPlanText}</>}/>
        );
    }

    private renderAvailablePaymentDaysBanner(): React.ReactNode {
        const daysAvailable: any = this.state.paymentDayOptions?.length
        if (this.state.loadingDays || daysAvailable === undefined || daysAvailable === 28) {
            return <></>
        } else if (daysAvailable === 0) {
            return (
                <WarningBanner wording={<>You can not change your payment date as it will mean the last monthly payment will be after the policy end date.</>}/>
            );
        } else {
            const lastPaymentDayOption: any = this.state.paymentDayOptions[daysAvailable - 1];
            return (
                <WarningBanner wording={
                    <>
                        You can only choose from <b>1st</b> to <b>{lastPaymentDayOption}</b> of the month as a
                        payment date. Otherwise, your last monthly payment will be after policy end date.
                    </>
                }/>
            );
        }
    }

    private renderChangePaymentDatePanel(): React.ReactNode {
        return (
            <>
                <div className="panel">
                    <div className="form-group">
                        <label htmlFor="changePaymentDayQuestion">Which day of the month should we take your
                            payment?</label>
                        <div className="input-group">
                            <select id="changePaymentDayQuestion" className="form-control null"
                                    onChange={this.updatePaymentDay} value={this.state.newPaymentDay}
                                    disabled={!this.state.paymentDayOptions || this.state.paymentDayOptions?.length === 0}>
                                {this.renderPaymentDayOptions()}
                            </select>
                        </div>
                    </div>
                    <div className="dashed-10"/>
                    <label htmlFor="paymentScheduleTable" className="text-center mt-3">
                        <div className="direct-debit-panel-title">Payment schedule</div>
                    </label>
                    <div id="paymentScheduleTable" className="mb-3">
                        {this.state.combinedInstalmentsInfo && this.renderPaidAndLeftAmount()}
                        {this.state.combinedInstalmentsInfo &&
                            <PaymentSchedule
                                isOpen={this.state.isOpen}
                                instalments={this.state.combinedInstalmentsInfo.instalments}
                                isPaidOff={false}
                                interaction={this.state.policy?.interaction}

                            />}
                    </div>
                    <div className="text-center">
                        {!this.state?.isOpen ?
                            <button
                                className="btn-inner-link btn-text text-center cursor-pointer text-decoration-none mb-5"
                                onClick={this.toggleInstallmentPlan}>
                                + <span className="text-decoration-underline">View instalments plan</span>
                            </button>
                            :
                            <button
                                className="btn-inner-link btn-text text-center cursor-pointer text-decoration-none mb-5"
                                onClick={this.toggleInstallmentPlan}>
                                - <span className="text-decoration-underline">Hide instalments plan</span>
                            </button>
                        }
                    </div>
                </div>
            </>
        );
    }

    private updatePaymentDay(event: any): void {
        const selectedDate: number = parseInt(event.target.value, 10);
        const selectedDateValidated: number = isNaN(selectedDate) ? this.state.policy?.interaction.paymentDay : selectedDate;
        this.setState({newPaymentDay: selectedDateValidated},
            () => this.updatePreviewInstalmentPlan());
    }

    private updatePreviewInstalmentPlan(): void {
        this.customerPortalService.getInstalmentInfoPreview(this.props.params.policyNumber, this.state.newPaymentDay)
            .subscribe((instalmentInfoPreview: InstalmentsInfo) => {
                this.setState({
                    combinedInstalmentsInfo: instalmentInfoPreview
                })
            })
    }

    private toggleInstallmentPlan = (): void => {
        this.setState({
            isOpen: !this.state?.isOpen
        })
    }

    private renderPaymentDayOptions(): React.ReactNode {
        if (!this.state.paymentDayOptions) {
            return <option value={0}>Please select...</option>;
        }
        return (
            <>
                <option value={0}>Please select...</option>
                {this.state.paymentDayOptions.map((option: string, index: number) => {
                    return (
                        <option key={index} className={option} value={index + 1}>{option}</option>
                    )
                })}
            </>
        );
    }

    private renderNavigationButtons(): React.ReactNode {
        return (
            <>
                <div className="form-group">
                    <button type="button" className="btn btn-default btn-white-dashboard margin-y40"
                            onClick={this.cancelChanges}>
                        Cancel
                    </button>
                    <button type="button" className="btn btn-blue-dashboard float-right"
                            onClick={this.saveAndConfirm}
                            disabled={!this.state.paymentDayOptions || this.state.paymentDayOptions?.length === 0}>
                        Save and confirm
                    </button>
                </div>
            </>
        );
    }

    private saveAndConfirm(): void {

        const allowedPaymentDay: boolean = this.state.newPaymentDay !== 0;
        const interactionId: any = this.state.policy?.interaction?._id;

        this.setState({
            showOverlay: true
        });

        if (allowedPaymentDay) {
            this.customerPortalService.storePaymentDay(interactionId, this.state.newPaymentDay).subscribe(() => {
                window.sessionStorage.setItem("showPopup", "true");
                window.sessionStorage.setItem("referrerJourney", INTERACTION_PROCESS_MTA_CHANGE_PAYMENT_DAY);
                this.setState({
                    showNoDaySelectedAlert: false
                })

                this.props.navigate(`/dashboard/monthlyPaymentHistory/${this.policyNumber}`);
            });
        } else {
            this.setState({
                showNoDaySelectedAlert: true,
                showOverlay: false
            })
        }
    }

    private cancelChanges(): void {
        this.props.navigate(`/dashboard/monthlyPaymentHistory/${this.policyNumber}`);
    }

    private renderPaidAndLeftAmount(): React.ReactNode {
        const {paidAmount, totalAmount, paymentsDone, instalments} = this.state.combinedInstalmentsInfo;
        const instalmentsLeft = instalments.length - paymentsDone;
        const instalmentPayment = calculateInstallmentPayment(instalments);

        return (
            <div className="text-center">
                <p className="mb-0">
                    You have paid {getAmountWithDecimalDigits(paidAmount)} out of {getAmountWithDecimalDigits(totalAmount)}
                </p>
                <p>
                    ({instalmentsLeft} instalments left X {getAmountWithDecimalDigits(instalmentPayment)})
                </p>
            </div>
        );
    }

    private renderAlert(): React.ReactNode {
        const wording: string = "Please select a payment date to continue.";

        return (
            <NotificationModal show={this.state.showNoDaySelectedAlert} hide={this.closeNoPaymentDaySelectedModal}>
                {wording}
            </NotificationModal>
        )
    }

    private closeNoPaymentDaySelectedModal(): void {
        this.setState({
            showNoDaySelectedAlert: !this.state.showNoDaySelectedAlert
        })
    }
}

export const DirectDebitChangePaymentDayPage = withRouter<{}, IParams>(DirectDebitChangePaymentDayPagePlain)
